/**
 * PaymentProductsSection.tsx
 *
 * Products / materials line editor for the Charge Payment modal. Mirrors the
 * legacy `payment-material-display-ds.php` row-builder: Category → Item,
 * price, quantity, taxable, discount1/2, sales tax percentage. Totals update
 * live and feed back to the parent as the authoritative Amount.
 */
import { useEffect, useRef, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trash2, ShoppingCart, X, Plus } from 'lucide-react';
import {
  listQuoteItems,
  listQuoteItemCategories,
  listDiscounts,
  deletePaymentProduct,
  type PaymentDetailMaterial,
  type PaymentDiscount,
  type QuoteItem,
} from '@/services/payments.service';
import { MAX_GIFT_CARD_AMOUNT, clampGiftCardAmount } from '@/lib/giftCards';
import { Switch } from '@/components/ui/Switch';
import { SearchableCombobox, type ComboboxOption } from '@/components/ui/SearchableCombobox';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { DraftNumberInput } from '@/pages/quotes/sections/DraftNumberInput';
import { descriptionToText } from '@/lib/utils';

export interface ProductLine {
  /** `paymentMaterialID` if this row is an edit of an existing line. */
  paymentMaterialID?: number;
  itemId: number;
  name: string;
  description: string;
  unit: string;
  price: number;
  quantity: number;
  taxable: boolean;
  salesTax: number;
  discount1Id: number | null;
  manualDiscount1: number;
  discount2Id: number | null;
  manualDiscount2: number;
  productType: number;
  /** For gift card SKUs (productType = 1): credit value stored on the card. */
  creditValue?: number;
  /** For gift card SKUs (productType = 1): the new card serial number. */
  serialNumber?: string;
  /** For type-999 SKUs: the patient the gift card credits. */
  targetGiftCardPatientId?: number;
  /**
   * When the line was prefilled from an appointment's required-products
   * bucket, this is the originating SAP row ID so the backend can link the
   * resulting `payment_materials` row back and flip the SAP `type` from
   * `pending` to `payment`. Mirrors the SAP-update tail of
   * `payment-consume-product2.php` lines 533-660.
   */
  scheduledAppointmentProductsId?: number;
  /**
   * Multi-SAP linkage used when the prefill row was grouped across N
   * pending slots for the same product type. The backend creates one
   * `payment_materials` row for the group and links every SAP id back
   * to that material so all N slots flip from `pending` to `payment`.
   */
  scheduledAppointmentProductsIds?: number[];
  /** When true, the price was set by the appointment prefill and should not be editable. */
  priceLocked?: boolean;
  /** When true, the catalog item allows staff to edit its price at point-of-charge. */
  allowPriceOverride?: boolean;
  /** The non-member (retail) price, set when the product has a member rate. */
  retailPrice?: number | null;
  /** Per-unit savings amount (retail - member). Zero when no member rate applies. */
  memberSavingsPerUnit?: number;
  /**
   * Shipping fee this product carries, captured from the catalog when the line
   * was added. Shipping is NOT a line item — `useShippingFees` reads this off
   * the lines to decide which distinct fees to charge once on the payment, and
   * the fee is rendered in the totals block, not here.
   */
  shippingFee?: { id: number; name: string; amount: number } | null;
}

export interface PaymentProductsSectionProps {
  /** When editing an existing payment, preseed the rows from the detail modal. */
  initialLines?: PaymentDetailMaterial[];
  /** Pre-seeded product lines from the appointment purchase flow (already ProductLine[]). */
  prefillLines?: ProductLine[];
  /** The facility's tax rate as a percentage (e.g. 8.25 for 8.25%). */
  facilityTaxRate?: number;
  /** Called on every change so the charge modal can recompute the amount. */
  onChange: (lines: ProductLine[]) => void;
  /** When set, enables server-side delete of line items. */
  paymentId?: number;
  /** When true, lines are display-only (no add/edit/delete). */
  readOnly?: boolean;
  /** When true, newly added products use member pricing when available. */
  isMember?: boolean;
  /** When true, the member discount option appears in the dropdown (even if not auto-applied). */
  showAsMember?: boolean;
}

/** Compute post-tax + post-discount total for a single row. */
export function computeLineTotal(line: ProductLine, discounts: PaymentDiscount[]): number {
  const taxAmount = line.taxable && line.salesTax > 0 && line.price > 0
    ? (line.salesTax / 100) * line.price
    : 0;
  let amount = (line.price + taxAmount) * Math.max(line.quantity, 0);

  const applyDiscount = (id: number | null, manual: number): number => {
    if (!id) return 0;
    const disc = discounts.find((d) => d.paymentDiscountID === id);
    if (!disc) return manual > 0 ? manual : 0;
    if (disc.discountType === 1) return manual;
    if (disc.discountType === 2) return amount * (disc.discountAmount / 100);
    if (disc.discountType === 3) return manual;
    if (disc.discountType === 4) return manual;
    return 0;
  };

  const d1 = applyDiscount(line.discount1Id, line.manualDiscount1);
  amount -= d1;
  const d2 = applyDiscount(line.discount2Id, line.manualDiscount2);
  amount -= d2;

  return Math.max(amount, 0);
}

/**
 * Whether staff may edit a line's unit price. Gift cards (productType 1) are
 * always editable because the price *is* the loaded credit; standard products
 * are editable only when their catalog item is flagged `allowPriceOverride`.
 * An appointment-prefilled line (`priceLocked`) is never editable, regardless.
 */
export function isPriceEditable(line: ProductLine): boolean {
  if (line.priceLocked) return false;
  return line.productType === 1 || line.allowPriceOverride === true;
}

/**
 * Consolidate disbursement-split siblings back into one logical line for
 * read-only display. Disbursing a purchased line splits its single
 * `payment_materials` row into N rows of quantity 1 (API
 * `PaymentExtrasController::disburseProduct`), each copying the price and the
 * whole-line discount verbatim. Rendered raw, that shows N rows and applies
 * the discount N times. We regroup rows that share every pricing/discount
 * attribute, summing only the quantity and keeping the single (whole-line)
 * discount, so the line reads as originally purchased. Display-only — the
 * editor never mutates through this, and the save payload keeps the raw rows.
 */
function consolidateLines(lines: ProductLine[]): ProductLine[] {
  const groups = new Map<string, ProductLine>();
  const order: string[] = [];
  for (const line of lines) {
    const key = [
      line.itemId,
      line.price,
      line.taxable ? 1 : 0,
      line.salesTax,
      line.discount1Id ?? 0,
      line.discount2Id ?? 0,
      line.manualDiscount1,
      line.manualDiscount2,
      line.name,
      line.description,
    ].join('|');
    const existing = groups.get(key);
    if (existing) {
      existing.quantity += line.quantity;
    } else {
      groups.set(key, { ...line });
      order.push(key);
    }
  }
  return order.map((k) => groups.get(k)!);
}

function getDiscountDisplay(disc: PaymentDiscount, manual: number): { prefix: string; name: string; suffix: string } {
  if (disc.discountType === 2) return { prefix: `(${disc.discountAmount}%)`, name: disc.title, suffix: '' };
  if (disc.discountType === 4 && manual > 0) return { prefix: '', name: disc.title, suffix: `(-$${manual.toFixed(2)})` };
  if ((disc.discountType === 1 || disc.discountType === 3) && manual > 0) return { prefix: `(-$${manual.toFixed(2)})`, name: disc.title, suffix: '' };
  return { prefix: '', name: disc.title, suffix: '' };
}

function isManualType(disc: PaymentDiscount): boolean {
  return disc.discountType === 1 || disc.discountType === 3 || disc.discountType === 4;
}

export function PaymentProductsSection({
  initialLines,
  prefillLines,
  facilityTaxRate,
  onChange,
  paymentId,
  readOnly = false,
  isMember = false,
  showAsMember,
}: PaymentProductsSectionProps) {
  const memberDiscountVisible = showAsMember ?? isMember;
  const [lines, setLines] = useState<ProductLine[]>(prefillLines ?? []);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [discountPopover, setDiscountPopover] = useState<{
    lineIndex: number;
    slot: 1 | 2 | 'new';
    discountId: number | null;
    amount: string;
  } | null>(null);

  const productListRef = useRef<HTMLDivElement>(null);
  const [listMinHeight, setListMinHeight] = useState<number | undefined>(undefined);
  const capturedEmpty = useRef(false);
  useEffect(() => {
    if (lines.length === 0 && !capturedEmpty.current && productListRef.current) {
      setListMinHeight(productListRef.current.offsetHeight);
      capturedEmpty.current = true;
    }
  });

  const { data: items = [], isLoading: loadingItems } = useQuery({
    queryKey: ['payment-quote-items-all'],
    queryFn: () => listQuoteItems({}),
    enabled: !readOnly,
  });

  const { data: rawCategories = [] } = useQuery({
    queryKey: ['payment-quote-item-categories'],
    queryFn: () => listQuoteItemCategories(),
    enabled: !readOnly,
  });

  const categories = useMemo(
    () => rawCategories.filter((cat) => !cat.title.includes('----') && !cat.title.toLowerCase().includes('admin use only')),
    [rawCategories],
  );

  const adminCategoryIds = useMemo(
    () => new Set(rawCategories.filter((cat) => cat.title.toLowerCase().includes('admin use only')).map((cat) => cat.categoryId)),
    [rawCategories],
  );

  const categoryOptions = useMemo<ComboboxOption<number>[]>(
    () => categories.map((cat) => ({ value: cat.categoryId, label: cat.title })),
    [categories],
  );

  const { data: discounts = [] } = useQuery({
    queryKey: ['payment-discounts'],
    queryFn: () => listDiscounts(),
  });



  useEffect(() => {
    if (!initialLines) return;
    setLines(
      initialLines.map((m) => ({
        paymentMaterialID: m.paymentMaterialID,
        itemId: m.itemId,
        name: m.name ?? '',
        description: m.description ?? '',
        unit: '',
        price: m.price,
        quantity: m.quantity || 1,
        taxable: m.taxable,
        salesTax: m.salesTax,
        discount1Id: m.discount1Id,
        manualDiscount1: m.manualDiscount1,
        discount2Id: m.discount2Id,
        manualDiscount2: m.manualDiscount2,
        productType: 0,
        serialNumber: m.serialNumber ?? undefined,
      })),
    );
  }, [initialLines]);

  useEffect(() => {
    if (prefillLines && prefillLines.length > 0) {
      setLines(prefillLines);
    }
  }, [prefillLines]);

  useEffect(() => {
    onChange(lines);
  }, [lines, onChange]);

  // Read-only views (an existing payment's detail) collapse disbursement-split
  // siblings into one row each; the editable flows render lines as-is. `onChange`
  // above always emits the raw `lines`, so the save payload is unaffected.
  const displayLines = useMemo(
    () => (readOnly ? consolidateLines(lines) : lines),
    [readOnly, lines],
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    let taxTotal = 0;
    let discountTotal = 0;
    let memberDiscountTotal = 0;
    let grand = 0;

    const isMemberDiscount = (id: number | null): boolean => {
      if (!id) return false;
      const disc = discounts.find((d) => d.paymentDiscountID === id);
      return disc?.discountType === 4;
    };

    for (const line of displayLines) {
      const lineSubtotal = line.price * Math.max(line.quantity, 0);
      const lineTax = line.taxable && line.salesTax > 0 && line.price > 0
        ? (line.salesTax / 100) * line.price * Math.max(line.quantity, 0)
        : 0;
      const lineGrand = computeLineTotal(line, discounts);
      subtotal += lineSubtotal;
      taxTotal += lineTax;
      grand += lineGrand;

      const totalLineDiscount = Math.max(lineSubtotal + lineTax - lineGrand, 0);
      let lineMemberDiscount = 0;
      if (isMemberDiscount(line.discount1Id)) lineMemberDiscount += line.manualDiscount1;
      if (isMemberDiscount(line.discount2Id)) lineMemberDiscount += line.manualDiscount2;
      lineMemberDiscount = Math.min(lineMemberDiscount, totalLineDiscount);
      memberDiscountTotal += lineMemberDiscount;
      discountTotal += totalLineDiscount - lineMemberDiscount;
    }
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxTotal: Math.round(taxTotal * 100) / 100,
      discountTotal: Math.round(discountTotal * 100) / 100,
      memberDiscountTotal: Math.round(memberDiscountTotal * 100) / 100,
      grand: Math.round(grand * 100) / 100,
    };
  }, [displayLines, discounts]);

  const addItem = (item: QuoteItem) => {
    const hasMemberPrice = memberDiscountVisible && item.memberPrice != null && item.memberPrice > 0;
    const savingsPerUnit = hasMemberPrice ? item.price - item.memberPrice! : 0;
    const memberDisc = discounts.find((d) => d.discountType === 4);
    const autoApply = isMember && hasMemberPrice && memberDisc != null;
    const memberDiscId = autoApply ? memberDisc!.paymentDiscountID : null;
    const isGiftCard = item.productType === 1;
    const qty = 1;

    setLines((prev) => [
      ...prev,
      {
        itemId: item.itemId,
        name: item.name,
        description: item.description,
        unit: item.unit,
        price: isGiftCard ? 0 : item.price,
        quantity: qty,
        taxable: item.taxable,
        salesTax: item.taxable ? (facilityTaxRate ?? 0) : 0,
        discount1Id: memberDiscId,
        manualDiscount1: memberDiscId ? savingsPerUnit * qty : 0,
        discount2Id: null,
        manualDiscount2: 0,
        productType: item.productType,
        creditValue: isGiftCard ? 0 : undefined,
        retailPrice: hasMemberPrice ? item.price : null,
        memberSavingsPerUnit: savingsPerUnit,
        allowPriceOverride: item.allowPriceOverride,
        shippingFee: item.shippingFee,
      },
    ]);
  };

  const comboboxOptions = useMemo<ComboboxOption<number>[]>(
    () =>
      items
        .filter((item: QuoteItem) => !item.name.includes('----'))
        .filter((item: QuoteItem) => {
          if (selectedCategoryId !== null) return item.categoryId === selectedCategoryId;
          return !item.categoryId || !adminCategoryIds.has(item.categoryId);
        })
        .map((item: QuoteItem) => {
          const hasMember = memberDiscountVisible && item.memberPrice != null && item.memberPrice > 0;
          const savings = hasMember ? item.price - item.memberPrice! : 0;
          return {
            value: item.itemId,
            label: item.name,
            meta: hasMember
              ? `$${item.price.toFixed(2)} (save $${savings.toFixed(2)})`
              : `$${item.price.toFixed(2)}`,
            group: item.categoryTitle ?? 'Other',
          };
        }),
    [items, selectedCategoryId, adminCategoryIds, memberDiscountVisible],
  );

  const updateLine = (index: number, patch: Partial<ProductLine>) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const removeLine = async (index: number) => {
    const line = lines[index];
    if (paymentId && line.paymentMaterialID) {
      try {
        await deletePaymentProduct(paymentId, line.paymentMaterialID);
      } catch (e) {
        console.warn('Failed to delete material line on server', e);
      }
    }
    setLines((prev) => prev.filter((_, i) => i !== index));
  };


  const openDiscountPopover = (lineIndex: number, slot: 1 | 2 | 'new') => {
    const line = lines[lineIndex];
    if (slot === 'new') {
      setDiscountPopover({ lineIndex, slot: 'new', discountId: null, amount: '' });
    } else {
      const id = slot === 1 ? line.discount1Id : line.discount2Id;
      const manual = slot === 1 ? line.manualDiscount1 : line.manualDiscount2;
      setDiscountPopover({ lineIndex, slot, discountId: id, amount: manual > 0 ? String(manual) : '' });
    }
  };

  const applyDiscount = () => {
    if (!discountPopover || !discountPopover.discountId) return;
    const { lineIndex, slot, discountId, amount } = discountPopover;
    const numAmount = Number(amount) || 0;

    if (slot === 'new') {
      const line = lines[lineIndex];
      if (!line.discount1Id) {
        updateLine(lineIndex, { discount1Id: discountId, manualDiscount1: numAmount });
      } else {
        updateLine(lineIndex, { discount2Id: discountId, manualDiscount2: numAmount });
      }
    } else if (slot === 1) {
      updateLine(lineIndex, { discount1Id: discountId, manualDiscount1: numAmount });
    } else {
      updateLine(lineIndex, { discount2Id: discountId, manualDiscount2: numAmount });
    }
    setDiscountPopover(null);
  };

  const removeDiscount = (lineIndex: number, slot: 1 | 2) => {
    if (slot === 1) {
      updateLine(lineIndex, { discount1Id: null, manualDiscount1: 0 });
    } else {
      updateLine(lineIndex, { discount2Id: null, manualDiscount2: 0 });
    }
    setDiscountPopover(null);
  };

  const renderDiscountPopover = () => {
    if (!discountPopover) return null;
    const line = lines[discountPopover.lineIndex];
    const selectedDisc = discountPopover.discountId
      ? discounts.find((d) => d.paymentDiscountID === discountPopover.discountId)
      : null;
    const needsAmount = selectedDisc ? isManualType(selectedDisc) : false;
    const isMemberDiscSelected = selectedDisc?.discountType === 4;

    const hasMemberSavings = (line?.memberSavingsPerUnit ?? 0) > 0;
    const memberAlreadyApplied = discounts.some(
      (d) => d.discountType === 4 && (d.paymentDiscountID === line?.discount1Id || d.paymentDiscountID === line?.discount2Id),
    );
    const availableDiscounts = [
      ...discounts.filter((d) => d.discountType === 4 && memberDiscountVisible && !memberAlreadyApplied),
      ...discounts.filter((d) => d.discountType !== 4),
    ];

    return (
      <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
        <div className="space-y-2">
          <select
            className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
            value={discountPopover.discountId ?? ''}
            onChange={(e) => {
              const id = Number(e.target.value) || null;
              const disc = id ? discounts.find((d) => d.paymentDiscountID === id) : null;
              const autoAmount = disc?.discountType === 4 && line
                ? ((line.memberSavingsPerUnit ?? 0) * Math.max(line.quantity, 1)).toFixed(2)
                : '';
              setDiscountPopover((prev) => prev ? { ...prev, discountId: id, amount: autoAmount } : null);
            }}
          >
            <option value="">Select discount...</option>
            {availableDiscounts.map((d) => {
              let label = d.title;
              if (d.discountType === 4 && hasMemberSavings) {
                label = `${d.title} (-$${((line?.memberSavingsPerUnit ?? 0) * Math.max(line?.quantity ?? 1, 1)).toFixed(2)})`;
              }
              return (
                <option key={d.paymentDiscountID} value={d.paymentDiscountID}>{label}</option>
              );
            })}
          </select>
          {needsAmount && !isMemberDiscSelected && (
            <input
              type="text"
              inputMode="decimal"
              placeholder="Amount"
              value={discountPopover.amount}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                  setDiscountPopover((prev) => prev ? { ...prev, amount: v } : null);
                }
              }}
              className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
            />
          )}
          {isMemberDiscSelected && (
            <div className="text-[11px] text-emerald-600 px-1">
              Saves ${Number(discountPopover.amount || 0).toFixed(2)} ({(line?.memberSavingsPerUnit ?? 0).toFixed(2)}/unit × {line?.quantity ?? 1})
            </div>
          )}
          <div className="flex items-center justify-between gap-1">
            {discountPopover.slot !== 'new' && (
              <button
                type="button"
                className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                onClick={() => removeDiscount(discountPopover.lineIndex, discountPopover.slot as 1 | 2)}
              >
                Remove
              </button>
            )}
            <div className="ml-auto flex gap-1">
              <button
                type="button"
                className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                onClick={() => setDiscountPopover(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={!discountPopover.discountId}
                onClick={applyDiscount}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Discount badge helper used for both editable and read-only rows ──
  const renderDiscountBadges = (line: ProductLine, index: number, editable: boolean) => {
    const badges: React.ReactNode[] = [];
    let showPopover = false;

    const slots: { id: number | null; manual: number; slot: 1 | 2; idKey: 'discount1Id' | 'discount2Id' }[] = [
      { id: line.discount1Id, manual: line.manualDiscount1, slot: 1, idKey: 'discount1Id' },
      { id: line.discount2Id, manual: line.manualDiscount2, slot: 2, idKey: 'discount2Id' },
    ];

    for (const s of slots) {
      if (!s.id) continue;
      const disc = discounts.find((d) => d.paymentDiscountID === s.id);
      if (!disc) continue;
      if (editable && discountPopover?.lineIndex === index && discountPopover?.slot === s.slot) showPopover = true;

      const isMemberDisc = disc.discountType === 4;
      const display = getDiscountDisplay(disc, s.manual);
      const colorClasses = isMemberDisc
        ? `bg-emerald-50 border-emerald-200 text-emerald-800${editable ? ' hover:bg-emerald-100' : ''}`
        : `bg-blue-50 border-blue-200 text-blue-800${editable ? ' hover:bg-blue-100' : ''}`;
      const xColor = isMemberDisc ? 'text-emerald-500 hover:text-emerald-700' : 'text-blue-500 hover:text-blue-700';

      badges.push(
        <span
          key={`d${s.slot}`}
          className={`inline-flex items-center gap-0.5 min-w-0 max-w-full rounded border px-1.5 py-0.5 text-[11px] ${
            editable ? 'cursor-pointer' : ''
          } ${colorClasses}`}
          onClick={() => { if (editable && !isMemberDisc) openDiscountPopover(index, s.slot); }}
        >
          {display.prefix && <span className="shrink-0 font-medium">{display.prefix}</span>}
          <span className="truncate">{display.name}</span>
          {display.suffix && <span className="shrink-0 font-medium">{display.suffix}</span>}
          {editable && (
            <button
              type="button"
              className={`shrink-0 ml-0.5 ${xColor}`}
              onClick={(e) => { e.stopPropagation(); removeDiscount(index, s.slot); }}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>,
      );
    }

    const appliedCount = (line.discount1Id ? 1 : 0) + (line.discount2Id ? 1 : 0);
    if (editable && appliedCount < 2) {
      const isAdding = discountPopover?.lineIndex === index && discountPopover?.slot === 'new';
      if (isAdding) showPopover = true;
      badges.push(
        isAdding ? (
          <span key="add" className="shrink-0 inline-flex items-center gap-0.5 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700">
            Applying discount…
          </span>
        ) : (
          <button
            key="add"
            type="button"
            className="shrink-0 inline-flex items-center gap-0.5 rounded border border-dashed border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-500 hover:border-slate-400 hover:text-slate-700"
            onClick={() => openDiscountPopover(index, 'new')}
          >
            <Plus className="h-3 w-3" /> Discount
          </button>
        ),
      );
    }

    return (
      <div className="relative min-w-0">
        <div className="flex items-center justify-end gap-1 overflow-hidden">
          {badges.length > 0 ? badges : <span className="text-xs text-slate-400">—</span>}
        </div>
        {showPopover && renderDiscountPopover()}
      </div>
    );
  };

  // ── Item name cell (shared between read-only and editable) ──
  const renderItemName = (line: ProductLine) => (
    descriptionToText(line.description) ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="break-words font-medium text-slate-900 cursor-default border-b border-dashed border-slate-300 inline-block">{line.name}</div>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={4} className="max-w-xs px-3 py-2">
          <p className="text-[11px] font-semibold text-white/80 uppercase tracking-wide mb-0.5">{line.name}</p>
          <p className="text-xs text-white/90 whitespace-pre-wrap">{descriptionToText(line.description)}</p>
        </TooltipContent>
      </Tooltip>
    ) : (
      <div className="break-words font-medium text-slate-900">{line.name}</div>
    )
  );

  return (
    <div className="space-y-3">
      {!readOnly ? (
        /* A hard 200px track beside a `1fr` one. At 375px the modal body's px-4 leaves
           ~343px, so the Product combobox got ~131px — the second reason this modal's
           content was wider than the phone. Single column below `md`. */
        <div className="grid grid-cols-[200px_1fr] gap-3 max-md:grid-cols-1">
          <div className="space-y-1 text-sm text-slate-700">
            <span className="font-medium">Category</span>
            <SearchableCombobox<number>
              mode="single"
              options={categoryOptions}
              value={selectedCategoryId}
              onChange={(val) => setSelectedCategoryId(val)}
              clearable
              placeholder="All Categories"
              emptyMessage="No matching categories"
            />
          </div>
          <label className="space-y-1 text-sm text-slate-700">
            <span className="font-medium">Product</span>
            <SearchableCombobox<number>
              mode="multi-add"
              options={comboboxOptions}
              loading={loadingItems}
              searchByLabelOnly
              closeOnAdd
              placeholder="Search by product name..."
              emptyMessage={selectedCategoryId ? 'No products in this category' : 'No matching products'}
              /*
                `addedValues` drives the "added" badge beside an option — SCRUM-1411's
                "there is no visual confirmation that the product was added while the
                dropdown is still open".

                `allowRepeat` keeps those options addable. The same product can be a
                second line because discounts are per line: staff discount one unit
                and not the other by adding it twice, which a quantity of 2 on one row
                cannot express.
              */
              addedValues={lines.map((l) => l.itemId)}
              allowRepeat
              onAdd={(opt) => {
                const item = items.find((i) => i.itemId === opt.value);
                if (item) addItem(item);
              }}
            />
          </label>
        </div>
      ) : null}

      <div>
      {/*
        SCRUM-1411. The table is `table-fixed` with a percentage colgroup, so it never
        overflows — it *crushes*. At 375px inside the modal body, Price at 9%, Qty at 7%
        and Total at 9% resolve to roughly 34px, 26px and 34px, which is why the values
        "sit on top of one another", the delete icon overlaps the total, and the headers
        read as "PRICEQTYTOTAL". The product name wrapping one word per line is the 18%
        Item column doing the same thing.

        Below `md` the table gets a real minimum width and scrolls inside its own box.
        Card mode was the other option and is a much larger change here: these rows carry
        editable price and quantity inputs, per-line discount rows and a tax toggle, so
        restructuring them is a rewrite rather than a re-layout. Containing the scroll
        also fixes the second half of the report — the cart was what made the modal wider
        than the phone and shifted the whole page sideways.
      */}
      <div
        ref={productListRef}
        className="rounded-t-md border border-slate-200 max-md:overflow-x-auto"
        style={listMinHeight ? { minHeight: listMinHeight } : undefined}
      >
        {displayLines.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <ShoppingCart className="h-8 w-8 text-slate-300" />
            <p className="mt-2 text-sm font-medium text-slate-500">No products added</p>
            <p className="text-xs text-slate-400">Search above to add products to this charge</p>
          </div>
        ) : (
          <table className="w-full table-fixed text-sm max-md:min-w-[34rem]">
            <colgroup>
              <col style={{ width: '18%' }} />
              <col style={{ width: readOnly ? '42%' : '38%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '9%' }} />
              {!readOnly && <col style={{ width: '4%' }} />}
            </colgroup>
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-2 py-1 text-left">Item</th>
                <th className="px-2 py-1"></th>
                <th className="px-2 py-1 text-center">Tax</th>
                <th className="px-2 py-1 text-right">Price</th>
                <th className="px-2 py-1 text-right">Qty</th>
                <th className="px-1 py-1 text-right">Total</th>
                {!readOnly && <th className="px-1 py-1"></th>}
              </tr>
            </thead>
            <tbody>
              {displayLines.map((line, index) => {
                const total = computeLineTotal(line, discounts);

                if (readOnly) {
                  return (
                    <tr key={`line-${index}`} className="border-t border-slate-100 align-top">
                      <td className="px-2 py-1">
                        {renderItemName(line)}
                        {line.serialNumber ? (
                          <div className="mt-0.5 text-xs text-slate-500">Serial: {line.serialNumber}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-1">{renderDiscountBadges(line, index, false)}</td>
                      <td className="px-2 py-1 text-center text-xs text-slate-700">
                        {line.taxable ? `${line.salesTax}%` : '—'}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-slate-800">{Number(line.price).toFixed(2)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-slate-800">{line.quantity}</td>
                      <td className="px-1 py-1 text-right font-mono text-slate-900">${total.toFixed(2)}</td>
                    </tr>
                  );
                }

                return (
                  <tr key={`line-${index}`} className="border-t border-slate-100 align-top">
                    <td className="px-2 py-1">
                      {renderItemName(line)}
                      {line.productType === 1 && (
                        <input
                          type="text"
                          value={line.serialNumber ?? ''}
                          onChange={(e) => updateLine(index, { serialNumber: e.target.value })}
                          placeholder="Serial # (auto if blank)"
                          maxLength={100}
                          className="mt-1 w-full min-w-0 rounded border border-slate-200 px-1.5 py-0.5 text-xs"
                        />
                      )}
                    </td>
                    <td className="px-2 py-1">{renderDiscountBadges(line, index, true)}</td>
                    <td className="px-1 py-1 text-center align-middle">
                      <div className="inline-flex items-center gap-1">
                        <Switch
                          size="sm"
                          checked={line.taxable}
                          onChange={(checked) => updateLine(index, {
                            taxable: checked,
                            salesTax: checked ? (line.salesTax || facilityTaxRate || 0) : 0,
                          })}
                        />
                        {line.taxable && (
                          <div className="flex items-center gap-0.5">
                            <DraftNumberInput
                              value={line.salesTax}
                              allowNegative={false}
                              maxDecimals={2}
                              onCommit={(n) => updateLine(index, { salesTax: Math.min(100, n) })}
                              className="w-12 min-w-0 rounded border border-slate-200 px-1 py-0.5 text-right text-sm"
                            />
                            <span className="text-xs text-slate-400">%</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      {isPriceEditable(line) ? (
                        <DraftNumberInput
                          value={line.price}
                          allowNegative={false}
                          maxDecimals={2}
                          blankZero
                          onCommit={(n) => {
                            if (line.productType === 1) {
                              // Gift cards keep price == loaded credit, capped
                              // the same way as IssueGiftCardModal's Amount
                              // field: the cap is client-side only, so every
                              // field that loads a card has to apply it.
                              const capped = clampGiftCardAmount(n);
                              updateLine(index, { price: capped, creditValue: capped });
                            } else {
                              // A standard price-override product only sets price.
                              updateLine(index, { price: n });
                            }
                          }}
                          placeholder="0.00"
                          title={line.productType === 1
                            ? `Maximum $${MAX_GIFT_CARD_AMOUNT.toLocaleString('en-US')} per gift card`
                            : undefined}
                          className="w-full min-w-0 rounded border border-slate-200 px-1 py-0.5 text-right"
                        />
                      ) : (
                        <div className="w-full min-w-0 rounded border border-slate-200 bg-slate-100 px-1 py-0.5 text-right text-slate-500 tabular-nums">
                          {Number(line.price).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={line.quantity}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '');
                          const raw = Number(v) || 0;
                          updateLine(index, { quantity: raw });
                        }}
                        onBlur={() => {
                          const newQty = Math.max(1, line.quantity || 1);
                          const patch: Partial<ProductLine> = { quantity: newQty };
                          if ((line.memberSavingsPerUnit ?? 0) > 0) {
                            const memberDiscSlot = [1 as const, 2 as const].find((slot) => {
                              const id = slot === 1 ? line.discount1Id : line.discount2Id;
                              if (!id) return false;
                              return discounts.some((d) => d.paymentDiscountID === id && d.discountType === 4);
                            });
                            if (memberDiscSlot === 1) patch.manualDiscount1 = line.memberSavingsPerUnit! * newQty;
                            if (memberDiscSlot === 2) patch.manualDiscount2 = line.memberSavingsPerUnit! * newQty;
                          }
                          updateLine(index, patch);
                        }}
                        className="w-full min-w-0 rounded border border-slate-200 px-1 py-0.5 text-right"
                      />
                    </td>
                    <td className="px-1 py-1 text-right font-mono text-slate-900">${total.toFixed(2)}</td>
                    <td className="px-1 py-1 text-center">
                      {/* 14px icon with no tap area and no accessible name. The
                          touch utilities are gated on `(pointer: coarse)`, so the
                          desktop control keeps its size. */}
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 touch-target-44 touch-target-inset"
                        onClick={() => removeLine(index)}
                        title="Remove"
                        aria-label="Remove product"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* `w-full` WITHOUT `table-fixed`, unlike the cart above — so this one uses auto
          layout and takes the greater of 100% and the sum of its cells' min-content
          widths. "Member Discount:" beside a font-mono currency figure can exceed a
          375px container, which is the other element that painted outside the modal.
          Same containment as the cart, and the same min-width so the two line up. */}
      <div className="rounded-b-md border border-t-0 border-slate-200 max-md:overflow-x-auto">
        <table className="w-full text-sm max-md:min-w-[34rem]">
          <tbody>
            <tr className="bg-slate-50 text-xs">
              <td className="px-2 py-1 text-right font-semibold">Subtotal:</td>
              <td className="px-1 py-1 text-right font-mono w-[9%]">${totals.subtotal.toFixed(2)}</td>
              {!readOnly && <td className="w-[4%]" />}
            </tr>
            {totals.taxTotal > 0 ? (
              <tr className="bg-slate-50 text-xs">
                <td className="px-2 py-1 text-right">Tax:</td>
                <td className="px-1 py-1 text-right font-mono">${totals.taxTotal.toFixed(2)}</td>
                {!readOnly && <td />}
              </tr>
            ) : null}
            {totals.memberDiscountTotal > 0 ? (
              <tr className="bg-slate-50 text-xs">
                <td className="px-2 py-1 text-right text-emerald-700">Member Discount:</td>
                <td className="px-1 py-1 text-right font-mono text-emerald-700">-${totals.memberDiscountTotal.toFixed(2)}</td>
                {!readOnly && <td />}
              </tr>
            ) : null}
            {totals.discountTotal > 0 ? (
              <tr className="bg-slate-50 text-xs">
                <td className="px-2 py-1 text-right text-green-700">Discount:</td>
                <td className="px-1 py-1 text-right font-mono text-green-700">-${totals.discountTotal.toFixed(2)}</td>
                {!readOnly && <td />}
              </tr>
            ) : null}
            <tr className="border-t border-slate-200 bg-slate-100">
              <td className="px-2 py-1 text-right font-bold">Products Total:</td>
              <td className="px-1 py-1 text-right font-mono font-bold">${totals.grand.toFixed(2)}</td>
              {!readOnly && <td />}
            </tr>
          </tbody>
        </table>
      </div>
      </div>

    </div>
  );
}
